import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { updateHelpMode } from 'services/auth.service';
import { RootState } from 'store/store';
import { IntroKeys } from '../intro/intro-keys';
import { setHelpMode } from './app-data.slice';
import { createSafeAsyncThunk } from 'store/store.helper';

const SLICE_ID = 'app';

export type OS = 'mac' | 'win';
export type HeldKey = 'Control' | 'Shift' | null;

export enum EnumDragger {
  cell,
  ws,
  tsSlider,
  dndSbClip,
  panZoom,
}

export enum ModalEnum {
  none,
  replace,
  resetPass,
  projectShare,
  addProject,
  addFolder,
  ytReLogin,
  gpOpen,
  gpSubmit,
  fcUpgrade,
  subscription,
}

interface State {
  isAuthenticated: boolean | null;
  isTouchDevice: boolean;
  modal: ModalEnum;
  tutorials: boolean;
  intro: boolean | IntroKeys;
  os: OS;
  heldKey: HeldKey;
  zoomTracks: boolean;
  dragger: EnumDragger | null;
  appRestored: boolean;
  hideTooltipListener: Record<string, never>;
  editorAvailable: boolean;
  finalcutsAvailable: boolean;
  projectLoaded: boolean;
  beginnerMode: boolean;
  storyLoaded: boolean;
  storyModal: boolean | 'setup';
}

const initialState: State = {
  isAuthenticated: null,
  isTouchDevice: false,
  modal: ModalEnum.none,
  tutorials: false,
  intro: false,
  os: 'win',
  heldKey: null,
  dragger: null,
  zoomTracks: false,
  appRestored: false,
  editorAvailable: false,
  finalcutsAvailable: false,
  hideTooltipListener: {},
  projectLoaded: false,
  beginnerMode: false,
  storyLoaded: false,
  storyModal: false,
};

// T H U N K S

export const passHelpMode = createSafeAsyncThunk(SLICE_ID + '/passHelpMode', async (value: boolean, { dispatch }) => {
  await updateHelpMode(value);
  dispatch(setHelpMode(value));
});

// S L I C E

export const appSlice = createSlice({
  name: SLICE_ID,
  initialState,
  reducers: {
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setIsTouchDevice: (state, action: PayloadAction<boolean>) => {
      state.isTouchDevice = action.payload;
    },
    setTutorials: (state, action: PayloadAction<boolean>) => {
      state.tutorials = action.payload;
    },
    setIntro: (state, action: PayloadAction<State['intro']>) => {
      state.intro = action.payload;
    },
    setModal: (state, action: PayloadAction<ModalEnum>) => {
      state.modal = action.payload;
    },
    setOs: (state, action: PayloadAction<boolean>) => {
      state.os = action.payload ? 'mac' : 'win';
    },
    setZoomTracks: (state, action: PayloadAction<boolean>) => {
      state.zoomTracks = action.payload;
    },
    setAppRestored: (state, action: PayloadAction<boolean>) => {
      state.appRestored = action.payload;
    },
    setEditorAvailable: (state, action: PayloadAction<boolean>) => {
      state.editorAvailable = action.payload;
    },
    setFinalcutsAvailable: (state, action: PayloadAction<boolean>) => {
      state.finalcutsAvailable = action.payload;
    },
    setHideTooltipListener: (state) => {
      state.hideTooltipListener = {};
    },
    setProjectLoaded: (state, action: PayloadAction<boolean>) => {
      state.projectLoaded = action.payload;
    },
    setStoryLoaded: (state, action: PayloadAction<boolean>) => {
      state.storyLoaded = action.payload;
    },
    setBeginnerMode: (state, action: PayloadAction<boolean>) => {
      state.beginnerMode = action.payload;
    },
    setStoryModal: (state, action: PayloadAction<boolean | 'setup' | undefined>) => {
      state.storyModal = action.payload !== undefined ? action.payload : !state.storyModal;
    },
    setHeldKey: (state, action: PayloadAction<HeldKey>) => {
      state.heldKey = action.payload;
    },
    setDragger: (state, action: PayloadAction<State['dragger']>) => {
      state.dragger = action.payload;
    },
  },
});

export const {
  setOs,
  setIntro,
  setTutorials,
  setZoomTracks,
  setAppRestored,
  setIsTouchDevice,
  setEditorAvailable,
  setFinalcutsAvailable,
  setHideTooltipListener,
  setIsAuthenticated,
  setProjectLoaded,
  setBeginnerMode,
  setStoryLoaded,
  setStoryModal,
  setHeldKey,
  setDragger,
  setModal,
} = appSlice.actions;

// S E L E C T O R S

export const selectSbInitialization = (state: RootState) =>
  state.app.storyLoaded && state.app.projectLoaded && state.audio.waveIsReady;

export default appSlice.reducer;
